import React, { useMemo, useRef, useState } from 'react'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import { useParams, useOutletContext } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'

import Control from '../Controls'
import MapProvider from './IndoorProvider'
import { MapBuilding, ModalContainer } from './IndoorStyles'

const ICON_SIZE = 46

const MapViewBuilding = ({
  loading,
  breadcrumbProps = {},
  legendProps = {},
  floorsNavigationProps = {
    canSee: false,
    onPrev: () => {},
    onNext: () => {}
  },
  image,
  iconSize = ICON_SIZE,
  defaultGrayScale = true,
  showHeatmap = false,
  canSeeHeatmap = false,
  alignCenter = true,
  activeEquipment = null,
  transformPanningDisabled = false,
  onHeatmapClick = () => null,
  hasOpenCard = false,
  expanded = false,
  children
}) => {
  const [grayscale, setGrayscale] = useState(defaultGrayScale)
  const { deviceId } = useParams()
  const context = useOutletContext()
  const isExpanded = context?.isExpanded || expanded || false
  const [panningDisabled, setPanningDisabled] = useState(false)
  const imageSize = useMemo(() => [image.width, image.height], [image])
  const wrapperStyle = useMemo(() => {
    const style = { width: '100%', height: '100%' }
    return alignCenter ? { ...style, display: 'flex', justifyContent: 'center', alignItems: 'center' } : style
  }, [alignCenter])
  const transformRef = useRef(null)
  const popupAnchorRef = useRef(null)

  return (
    <MapBuilding 
      ref={transformRef} 
      collapse={deviceId ? 1 : 0} 
      isexpanded={isExpanded ? 1 : 0}
    >
      <Box
        ref={popupAnchorRef}
        sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 10 }}
      />
      <TransformWrapper
        initialScale={1}
        initialPositionX={0}
        initialPositionY={0}
        minScale={0.5}
        maxScale={32}
        limitToBounds={false}
        panning={{
          disabled: transformPanningDisabled
        }}
        wheel={{
          step: 0.05,
          disabled: false
        }}>
        {({ zoomIn, zoomOut, setTransform, state }) => {
          return (
            <>
              <Control
                width="100%"
                height="100%"
                zoomProps={{
                  mapsView: 'building',
                  onZoomIn: () => zoomIn(),
                  onZoomOut: () => zoomOut()
                }}
                floorsNavigationProps={floorsNavigationProps}
                heatmapProps={{
                  onClick: onHeatmapClick,
                  active: showHeatmap,
                  canSee: canSeeHeatmap
                }}
                grayscaleProps={{
                  grayscale,
                  onGrayscale: () => setGrayscale(prev => !prev)
                }}
                panningProps={{
                  locked: panningDisabled,
                  onLock: locked => {
                    setPanningDisabled(locked)
                  }
                }}
                legendProps={legendProps}
                breadcrumbProps={breadcrumbProps}
                activeEquipment={activeEquipment && <ModalContainer>{activeEquipment.content}</ModalContainer>}
              />
              <Box
                id="infoWindowBuilding"
                style={{
                  position: 'absolute',
                  left: state.positionX,
                  top: state.positionY
                }}
              />
              <TransformComponent wrapperStyle={wrapperStyle}>
                {
                  loading
                    ? <Box
                        position="absolute"
                        top={'calc(50% - 30px)'}
                        left={'calc(50% - 30px)'}>
                        <CircularProgress size={60} />
                      </Box>
                    : <MapProvider
                        transformRef={transformRef}
                        popupAnchorRef={popupAnchorRef}
                        transformWrapperState={state}
                        grayscale={grayscale}
                        showSvg={!showHeatmap}
                        image={image}
                        imageSize={imageSize}
                        iconSize={iconSize}
                        setTransform={setTransform}
                        hasOpenCard={hasOpenCard}
                      >
                        {children}
                      </MapProvider>
                }
              </TransformComponent>
            </>
          )
        }}
      </TransformWrapper>
    </MapBuilding>
  )
}

export default MapViewBuilding
